import type { TeamTextSoundSettings, TeamTextView } from '../api/client';

/**
 * A text for your ring group or queue (1.12.33, Teampace 2978; Joey: "I would like some kind of
 * message to play when there is a message from a queue or a ring group that comes in").
 *
 * - Its own sound, clearly unlike the chat sound (a counter bell: a customer is waiting), and a
 *   stronger one for urgent texts. Then, if they want it, the browser says "New text for Billing"
 *   ("Urgent text for Billing"). Never who texted or what they wrote.
 * - Whoever the server tells hears it: the person a queue gave it to, or everyone a ring group (or
 *   an urgent text) tells (TeamTexts.hearers on the server). The server leaves out anyone on do not
 *   disturb; this checks again here.
 * - Quiet: on do not disturb; for the conversation they're reading; in quiet hours while Stitch isn't
 *   in front (as for any message). On a call: one soft tone instead, without the spoken line.
 * - Reminders: the sound again every 30 seconds while it still waits and is still theirs to answer,
 *   up to 5 times. It stops as soon as someone takes it or it's answered.
 *
 * The choices are each person's own, kept by Stitch (Your settings → Team texts), so they follow
 * them to every device. This file decides; lib/teamSoundPlayer plays.
 */

export type TeamSoundPrefs = TeamTextSoundSettings;
export type TeamVolume = TeamSoundPrefs['volume'];

/** Everything on, at a medium volume, until they choose otherwise. */
export const DEFAULT_TEAM_SOUND: TeamSoundPrefs = { sound: true, speak: true, reminders: true, volume: 'medium' };

/** Every 30 seconds while it waits, at most 5 times. */
export const REMIND_MS = 30_000;
export const MAX_REMINDERS = 5;

/** Their choices as the server sent them (an older server sends none: the defaults). */
export function teamSoundPrefs(raw: Partial<TeamSoundPrefs> | null | undefined): TeamSoundPrefs {
  const r = raw ?? {};
  return {
    sound: r.sound !== false,
    speak: r.speak !== false,
    reminders: r.reminders !== false,
    volume: r.volume === 'low' || r.volume === 'high' ? r.volume : 'medium'
  };
}

/** The team a text is for, as its alert says. */
export interface TeamOfAlert {
  extension: string;
  name: string;
  urgent: boolean;
}

/**
 * The spoken line: "New text for Billing", "Urgent text for Billing". Only the team's name (as the
 * phone system has it), never the customer's number or words.
 */
export function spokenLine(team: Pick<TeamOfAlert, 'name' | 'urgent'>): string {
  const name = team.name.replace(/\s+/g, ' ').trim() || 'your team';
  return `${team.urgent ? 'Urgent' : 'New'} text for ${name}`;
}

/** The team text sound; urgent, stronger; on a call, one soft tone. 'message': the ordinary message sound (theirs is off). */
export type TeamSoundKind = 'team' | 'urgent' | 'soft';

/** What's going on here when a team text's alert arrives (or a reminder is due). */
export interface QuietCheck {
  /** Their own do not disturb, in that company. */
  dnd: boolean;
  /** On a call: the server says so (a desk phone, the app, a Patch, anywhere), or this device is on one. */
  onCall: boolean;
  /** Stitch is in front, on that conversation. */
  reading: boolean;
  /** Their quiet hours, while Stitch isn't in front (as for any message). */
  quietHours: boolean;
}

export interface AlertPlan {
  /** What to play (null: nothing; 'message': the ordinary message sound, theirs to switch off on this device). */
  sound: TeamSoundKind | 'message' | null;
  /** What to say after the sound (null: nothing). */
  speak: string | null;
  /** Start the reminders. */
  remind: boolean;
}

const NOTHING: AlertPlan = { sound: null, speak: null, remind: false };

/** What a team text's alert does here. Pure, so it can be tested. */
export function teamAlertPlan(prefs: TeamSoundPrefs, team: TeamOfAlert, q: QuietCheck): AlertPlan {
  if (q.dnd || q.reading || q.quietHours) return NOTHING;
  // Their team text sound off: the message sound, as for any message (nothing more).
  if (!prefs.sound) return { sound: 'message', speak: null, remind: false };
  if (q.onCall) return { sound: 'soft', speak: null, remind: prefs.reminders };
  return { sound: team.urgent ? 'urgent' : 'team', speak: prefs.speak ? spokenLine(team) : null, remind: prefs.reminders };
}

/**
 * Whether the text is still waiting and still theirs to answer: their latest text unanswered, and
 * either theirs, or nobody's yet (they were told, with the rest of the team). Taken by someone else,
 * answered, resolved, or moved to another team: no.
 */
export function stillYours(view: TeamTextView | null | undefined, team: Pick<TeamOfAlert, 'extension'>): boolean {
  if (!view || view.extension !== team.extension || view.state === 'resolved') return false;
  const owed = view.owed ?? Boolean(view.waitingSince);
  if (!owed) return false;
  // Signed out of the queue: its texts are read-only for them.
  if (!view.signedIn && !view.supervisor) return false;
  return view.mine || (view.state === 'waiting' && !view.owner);
}

/** A reminder schedule: when the alert came (this device's clock) and how many turns have gone. */
export interface Reminder {
  startedAt: number;
  turns: number;
}

/** When the next reminder is due (null: no more). Every 30 seconds from the alert, 5 at most. */
export function nextReminderAt(r: Reminder): number | null {
  return r.turns >= MAX_REMINDERS ? null : r.startedAt + (r.turns + 1) * REMIND_MS;
}

/**
 * A reminder's turn: play it, skip this one (quiet for now: on a call, reading it, do not disturb;
 * the turn still counts), or stop for good (no longer theirs, answered, switched off, or all 5 gone).
 */
export function reminderTurn(r: Reminder, prefs: TeamSoundPrefs, view: TeamTextView | null | undefined, team: Pick<TeamOfAlert, 'extension'>, q: QuietCheck): 'play' | 'skip' | 'stop' {
  if (!prefs.sound || !prefs.reminders || r.turns >= MAX_REMINDERS) return 'stop';
  if (!stillYours(view, team)) return 'stop';
  if (q.dnd || q.onCall || q.reading || q.quietHours) return 'skip';
  return 'play';
}
